import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from "@angular/router";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import "hammerjs";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { ToastrModule, ToastrService } from "ngx-toastr";
import { TranslateModule } from "@ngx-translate/core";

import { CoreModule } from "@core/core.module";
import { CoreCommonModule } from "@core/common.module";
import { CoreSidebarModule, CoreThemeCustomizerModule } from "@core/components";
import { CardSnippetModule } from "@core/components/card-snippet/card-snippet.module";

import { coreConfig } from "app/app-config";
import { AuthGuard } from "app/main/auth/helpers/auth.guards";
import { AppComponent } from "app/app.component";
import { LayoutModule } from "app/layout/layout.module";
import { ContentHeaderModule } from "app/layout/components/content-header/content-header.module";
import { NgxPermissionsModule } from "ngx-permissions";
import { BaseSharedModule } from "@shared/base-shared.module";
import { CoreLoadingScreenService } from "@core/services/loading-screen.service";
import { JwtInterceptor } from "./main/auth/helpers/jwt.interceptor";
import { ErrorInterceptor } from "@shared/interceptors/error.interceptor";
import { LoaderComponent } from "./Shared/loader/loader.component";
import { NgApexchartsModule } from "ng-apexcharts";
import { GeometricLocationGuard } from "./main/auth/helpers/GeometricLocation.guards";
import { LoadSettingResolver } from "./load-setting.resolver";
import { SharedTableComponent } from './main/shared/components/shared-table/shared-table.component';
import { NgxDatatableModule } from "@swimlane/ngx-datatable";


const appRoutes: Routes = [
  {
    path: "auth",
    loadChildren: () =>
      import("./main/auth/Auth.module").then((m) => m.AuthModule),
  },
  {
    path: '',
    resolve: { Settings: LoadSettingResolver },
    children: [
      {
        path: "home",
        loadChildren: () =>
          import("./main/home/home.module").then((m) => m.HomeModule),
        canActivate: [AuthGuard],
      },
      {
        path: "dashboard",
        loadChildren: () =>
          import("./main/dashboard/dashboard.module").then(
            (m) => m.DashboardModule
          ),
      },

      // {
      //   path: "employee",
      //   loadChildren: () =>
      //     import("./main/employee/employee.module").then((m) => m.EmployeeModule),
      //   canActivate: [AuthGuard],
      // },
      // Old employee module URLs (bookmarks, emailed links) → Employee Profile.
      { path: "employee/list", redirectTo: "/employee/employee-profile/list", pathMatch: "full" },
      { path: "employee/create", redirectTo: "/employee/employee-profile/create", pathMatch: "full" },
      { path: "employee/edit/:employeeId", redirectTo: "/employee/employee-profile/:employeeId", pathMatch: "full" },
      {
        path: "employee/employee-profile",
        loadChildren: () =>
          import("./main/employee-profile/employee-profile.module").then((m) => m.EmployeeProfileModule),
        canActivate: [AuthGuard],
      },
      {
        // employee/custom-fields and employee/groups
        path: "employee",
        loadChildren: () =>
          import("./main/employee-profile/custom-field/employee-custom-field.module").then((m) => m.EmployeeCustomFieldModule),
        canActivate: [AuthGuard],
      },
      {
        path: "leaves",
        loadChildren: () =>
          import("./main/leaves/leaves.module").then((m) => m.LeavesModule),
        canActivate: [AuthGuard],
      },
      {
        path: "time-management",
        loadChildren: () =>
          import("./main/time-mangement/times.module").then((m) => m.TimesModule),
        canActivate: [AuthGuard],
      },
      /*
      {
        path: 'time-management/:id',
        loadChildren: () => import('./main/time-mangement/time-mangement.module').then(m => m.TimeMangementModule)
      },*/
      {
        path: "roster",
        loadChildren: () =>
          import("./main/roster/roster.module").then((m) => m.RosterModule),
        canActivate: [AuthGuard],
      },
      {
        path: "profile",
        loadChildren: () =>
          import("./main/profile/profile.module").then((m) => m.ProfileModule),
        canActivate: [AuthGuard],
      },
      {
        path: "user",
        loadChildren: () =>
          import("./main/user/user.module").then((m) => m.UserModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/settings",
        loadChildren: () =>
          import("./main/setting/setting.module").then((m) => m.SettingModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/account",
        loadChildren: () =>
          import("./main/account/account.module").then((m) => m.AccountModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/department",
        loadChildren: () =>
          import("./main/department/department.module").then(
            (m) => m.DepartmentModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/holidays",
        loadChildren: () =>
          import("./main/holidays/holidays.module").then((m) => m.HolidaysModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/roster-times",
        loadChildren: () =>
          import("./main/roster-times/roster-times.module").then(
            (m) => m.RosterTimesModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/time-record-tags",
        loadChildren: () =>
          import("./main/time-record-tags/time-record-tags.module").then(
            (m) => m.TimeRecordTagsModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/time-record-tags-group",
        loadChildren: () =>
          import("./main/time-record-tags-group/time-record-tags-group.module").then(
            (m) => m.TimeRecordTagsGroupModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/employee-group",
        loadChildren: () =>
          import("./main/employee-class/employee-class.module").then(
            (m) => m.EmployeeClassModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/geometric-locations",
        loadChildren: () =>
          import("./main/geometric-location/geometric-locations.module").then(
            (m) => m.GeometricLocationsModule
          ),
        canActivate: [AuthGuard, GeometricLocationGuard],
      },
      {
        path: "setup/paye-type",
        loadChildren: () =>
          import("./main/paye-type/paye-type.module").then((m) => m.PayeTypeModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/banks",
        loadChildren: () =>
          import("./main/banks/banks.module").then((m) => m.BanksModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/bank-branches",
        loadChildren: () =>
          import("./main/bank-branches/bank-branches.module").then(
            (m) => m.BankBranchesModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/payment-source-accounts",
        loadChildren: () =>
          import("./main/payment-source-accounts/payment-source-accounts.module").then(
            (m) => m.PaymentSourceAccountsModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/job-codes",
        loadChildren: () =>
          import("./main/job-codes/job-codes.module").then((m) => m.JobCodesModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/job-titles",
        loadChildren: () =>
          import("./main/job-titles/job-titles.module").then(
            (m) => m.JobTitlesModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/contract-types",
        loadChildren: () =>
          import("./main/contract-type/contract-type.module").then(
            (m) => m.ContractTypeModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/qualification-types",
        loadChildren: () =>
          import("./main/qualification-type/qualification-type.module").then(
            (m) => m.QualificationTypeModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/automated-greetings",
        loadChildren: () =>
          import("./main/automated-greetings/automated-greetings.module").then(
            (m) => m.AutomatedGreetingsModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/leave-reason",
        loadChildren: () =>
          import("./main/leave-reason/leave-reason.module").then(
            (m) => m.LeaveReasonModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/groups",
        loadChildren: () =>
          import("./main/groups/groups.module").then((m) => m.GroupsModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/attachment-categories",
        loadChildren: () =>
          import("./main/attachment-category/attachment-category.module").then(
            (m) => m.AttachmentCategoryModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/payroll-account",
        loadChildren: () =>
          import("./main/payroll/account/account.module").then((m) => m.AccountModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/rate-types",
        loadChildren: () =>
          import("./main/payroll/rate-type/rate-type.module").then((m) => m.RateTypeModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/account-type",
        loadChildren: () =>
          import("./main/payroll/account-type/account-type.module").then((m) => m.AccountTypeModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/earning-deduction",
        loadChildren: () =>
          import("./main/payroll/earning-deduction/earning-deduction.module").then((m) => m.EarningDeductionModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/earning-deduction-config",
        loadChildren: () =>
          import("./main/payroll/earning-deduction-config/earning-deduction-config.module").then((m) => m.EarningDeductionConfigModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/vacation-pay-config",
        loadChildren: () =>
          import("./main/payroll/vacation-pay-config/vacation-pay-config.module").then((m) => m.VacationPayConfigModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/severance-pay-config",
        loadChildren: () =>
          import("./main/payroll/severance-pay-config/severance-pay-config.module").then((m) => m.SeverancePayConfigModule),
        canActivate: [AuthGuard],
      },
      {
        path: "payroll/severance-pay",
        loadChildren: () =>
          import("./main/payroll/severance-pay/severance-pay.module").then((m) => m.SeverancePayModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/employee-filter",
        loadChildren: () =>
          import("./main/payroll/employee-filter/employee-filter.module").then((m) => m.EmployeeFilterModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/formula-configuration",
        loadChildren: () =>
          import("./main/payroll/formula-configuration/formula-configuration.module").then((m) => m.FormulaConfigurationModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/general-ledger",
        loadChildren: () =>
          import("./main/payroll/general-ledger/general-ledger.module").then((m) => m.GeneralLedgerModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/kpi-classification",
        loadChildren: () =>
          import("./main/payroll/kpi-classification/kpi-classification.module").then((m) => m.KpiClassificationModule),
        canActivate: [AuthGuard],
      },
      {
        path: "setup/service-charge-config",
        loadChildren: () =>
          import("./main/payroll/service-charge-config/service-charge-config.module").then((m) => m.ServiceChargeConfigModule),
        canActivate: [AuthGuard],
      },

      {
        path: "administration",
        loadChildren: () =>
          import("./main/administration/administration.module").then(
            (m) => m.AdministrationModule
          ),
        canActivate: [AuthGuard],
      },

      {
        path: "pages",
        loadChildren: () =>
          import("./main/pages/pages.module").then((m) => m.PagesModule),
        canActivate: [AuthGuard],
      },

      {
        path: "components",
        loadChildren: () =>
          import("./main/components/components.module").then(
            (m) => m.ComponentsModule
          ),
        canActivate: [AuthGuard],
      },
      // {
      //   path: "extensions",
      //   loadChildren: () =>
      //     import("./main/extensions/extensions.module").then(
      //       (m) => m.ExtensionsModule
      //     ),
      //   canActivate: [AuthGuard],
      // },
      {
        path: 'reports',
        loadChildren: () => import('./main/reports/reports.module').then((m) => m.ReportsModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'requests',
        loadChildren: () => import('./main/requests/requests.module').then((x) => x.RequestsModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'payroll',
        loadChildren: () => import('./main/payroll/payroll.module').then((x) => x.PayrollModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'setup',
        loadChildren: () =>
          import('./main/general-settings/general-settings.module').then(
            (m) => m.GeneralSettingsModule
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "",
        redirectTo: "/dashboard",
        pathMatch: "full",
      },
      {
        path: "**",
        redirectTo: "/pages/miscellaneous/error", //Error 404 - Page not found
      }

    ]
  }
];

@NgModule({
  declarations: [
    AppComponent,
    // ContextMenuComponent,
    // BasicCustomContextMenuComponent,
    // AnimatedCustomContextMenuComponent,
    // SubMenuCustomContextMenuComponent,
    LoaderComponent,
    SharedTableComponent,
  ],
  bootstrap: [AppComponent], imports: [BrowserModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: "enabled",
      onSameUrlNavigation: "reload",
    }),
    NgbModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot(),
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,
    CardSnippetModule,
    LayoutModule,
    ContentHeaderModule,
    NgxPermissionsModule.forRoot(),
    BaseSharedModule,
    NgApexchartsModule,
    NgxDatatableModule], providers: [
      CoreLoadingScreenService,
      { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
      provideHttpClient(withInterceptorsFromDi()),
    ]
})
export class AppModule { }
